import {
    FaTimes,
    FaPhone,
    FaEnvelope,
    FaMapMarkerAlt,
    FaBriefcase,
    FaCalendarAlt,
    FaIdBadge,
    FaBuilding,
} from "react-icons/fa";
import Partner from "../models/Partner";
import { IconType } from "react-icons";

interface PartnerDetailsModalProps {
    partner: Partner;
    onClose: () => void;
}

export default function PartnerDetailsModal({
    partner,
    onClose,
}: PartnerDetailsModalProps) {
    const staffName = partner.user
        ? `${partner.user.firstName} ${partner.user.lastName}`.trim()
        : "Unknown staff";

    const status = partner.status || (partner.isActive ? "Active" : "Inactive");

    const formatDate = (date?: string) => {
        if (!date) return "—";

        return new Date(date).toLocaleDateString("en-NG", {
            day: "numeric",
            month: "short",
            year: "numeric",
        });
    };

    const getInitials = (name: string) => {
        const names = name.trim().split(/\s+/);

        if (names.length >= 2) {
            return `${names[0][0]}${names[1][0]}`.toUpperCase();
        }

        return names[0]?.slice(0, 2).toUpperCase() || "NA";
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/40 px-4 py-6 ">
            <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-900/20">

                {/* Header */}
                <div className="flex items-start justify-between border-b border-slate-100 px-5 py-4 sm:px-6">
                    <div className="flex min-w-0 items-center gap-3">
                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-blue-50 text-sm font-semibold text-blue-700">
                            {getInitials(partner.name)}
                        </div>

                        <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                                <h2 className="truncate text-base font-semibold text-slate-900">
                                    {partner.name}
                                </h2>

                                <span
                                    className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-semibold ${status === "Active"
                                        ? "bg-emerald-50 text-emerald-700"
                                        : "bg-slate-100 text-slate-500"
                                        }`}
                                >
                                    <span
                                        className={`h-1.5 w-1.5 rounded-full ${status === "Active"
                                            ? "bg-emerald-500"
                                            : "bg-slate-400"
                                            }`}
                                    />
                                    {status}
                                </span>
                            </div>

                            {partner.companyName && (
                                <p className="mt-0.5 truncate text-xs text-slate-500">
                                    {partner.companyName}
                                </p>
                            )}
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={onClose}
                        className="ml-3 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                        aria-label="Close"
                    >
                        {FaTimes({ size: 13 })}
                    </button>
                </div>

                {/* Body */}
                <div className="overflow-y-auto px-5 py-5 sm:px-6">

                    {/* Partner Information */}
                    <section>
                        <SectionTitle title="Partner information" />

                        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">

                            <DetailItem
                                icon={FaBriefcase}
                                label="Partner type"
                                value={capitalize(partner.type)}
                            />

                            <DetailItem
                                icon={FaIdBadge}
                                label="Specialty"
                                value={partner.specialty}
                            />

                            <DetailItem
                                icon={FaBuilding}
                                label="Company name"
                                value={partner.companyName}
                            />

                            <DetailItem
                                icon={FaPhone}
                                label="Phone"
                                value={partner.phone}
                            />

                            <DetailItem
                                icon={FaEnvelope}
                                label="Email"
                                value={partner.email}
                            />
                            <DetailItem
                                icon={FaCalendarAlt}
                                label="Added on"
                                value={formatDate(partner.createdAt)}
                            />

                            <DetailItem
                                icon={FaMapMarkerAlt}
                                label="Address"
                                value={partner.address}
                                fullWidth
                            />
                        </div>
                    </section>

                    {/* Description */}
                    {partner.description && (
                        <section className="mt-6">
                            <SectionTitle title="Description" />

                            <div className="mt-3 rounded-xl border border-slate-100 bg-slate-50/70 px-4 py-3">
                                <p className="whitespace-pre-wrap text-sm leading-6 text-slate-600">
                                    {partner.description}
                                </p>
                            </div>
                        </section>
                    )}

                    {/* Added By */}
                    <section className="mt-6">
                        <SectionTitle title="Added by" />

                        {partner.user ? (
                            <div className="mt-3 rounded-xl border border-slate-200 bg-white p-4">
                                <div className="flex items-center gap-3">
                                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600">
                                        {getInitials(staffName)}
                                    </div>

                                    <div className="min-w-0">
                                        <p className="truncate text-sm font-semibold text-slate-800">
                                            {staffName}
                                        </p>

                                        <p className="mt-0.5 text-xs text-slate-400">
                                            {partner.user?.staffId}
                                        </p>
                                    </div>
                                </div>

                                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                                    <DetailItem
                                        icon={FaEnvelope}
                                        label="Email"
                                        value={partner.user.email}
                                    />

                                    <DetailItem
                                        icon={FaPhone}
                                        label="Phone"
                                        value={partner.user.phone}
                                    />

                                    <DetailItem
                                        icon={FaBriefcase}
                                        label="Job role"
                                        value={partner.user.portfolio}
                                        fullWidth
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="mt-3 rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-4 text-center">
                                <p className="text-xs text-slate-400">
                                    Staff information is not available.
                                </p>
                            </div>
                        )}
                    </section>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end border-t border-slate-100 bg-slate-50/50 px-5 py-3 sm:px-6">
                    <button
                        type="button"
                        onClick={onClose}
                        className="h-9 rounded-lg border border-slate-200 bg-white px-4 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 hover:text-slate-800"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ---------------- Helpers ---------------- */

function SectionTitle({ title }: { title: string }) {
    return (
        <div className="flex items-center gap-2">
            <div className="h-4 w-1 rounded-full bg-blue-600" />
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                {title}
            </h3>
        </div>
    );
}

function DetailItem({
    icon: Icon,
    label,
    value,
    fullWidth = false,
}: {
    icon: IconType;
    label: string;
    value?: string;
    fullWidth?: boolean;
}) {
    return (
        <div
            className={`flex min-w-0 items-start gap-3 rounded-xl border border-slate-100 bg-slate-50/50 px-3.5 py-3 ${fullWidth ? "sm:col-span-2" : ""
                }`}
        >
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white text-slate-400 shadow-sm">
                {Icon({ size: 11 })}
            </div>

            <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                    {label}
                </p>

                <p
                    className={`
                        ${label.toLocaleLowerCase() === "job role" ? "capitalize" : ""}
                        mt-0.5 truncate text-sm 
                        font-medium text-slate-700
                        `}
                    title={value || "—"}
                >
                    {value || "—"}
                </p>
            </div>
        </div>
    );
}

function capitalize(value: string) {
    return value.charAt(0).toUpperCase() + value.slice(1);
}